import classNames from 'classnames';
import * as React from 'react';
import { MosaicKey } from 'react-mosaic-component/lib/types';

import { MosaicViewManager } from 'src/components/mosaic/MosaicViewManager';
import { MosaicCollapseOptionsById } from 'src/components/mosaic/types';
import {
  MosaicSplitOptions,
  MosaicSplitType,
  NestedMosaicParentInitial,
  ProjectSidebarTileId,
} from 'src/constants/mosaic';
import { ProjectResourceType } from 'src/constants/project';
import {
  ProjectResourceFileUploadCard,
  ProjectResourceModelCard,
  ProjectResourceVirtualGraphCard,
} from 'src/containers/Project/ProjectResourceCard';
import { ProjectSidebarSection } from 'src/containers/Project/ProjectSidebarSection';
import {
  modelResourceListSelector,
  projectResourceListSelector,
} from 'src/selectors/fullResourceList';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

const collapseOptions: MosaicCollapseOptionsById<ProjectSidebarTileId> = {
  [ProjectSidebarTileId.RESOURCES]: {
    collapsible: false,
    collapsed: false,
    collapsedSize: 220,
    collapsedVisible: true,
  },
  [ProjectSidebarTileId.MODELS]: {
    collapsible: false,
    collapsed: false,
    collapsedSize: 220,
    collapsedVisible: true,
  },
};

const initialValue: NestedMosaicParentInitial<ProjectSidebarTileId> = {
  direction: 'column',
  first: ProjectSidebarTileId.RESOURCES,
  second: ProjectSidebarTileId.MODELS,
};

const splitOptions: MosaicSplitOptions = {
  firstMinSplitType: MosaicSplitType.NUMBER,
  firstMinSplitValue: 220,
  secondMinSplitType: MosaicSplitType.NUMBER,
  secondMinSplitValue: 220,
  initialSplitPercentage: 50,
};

type ProjectSidebarProps = { id: MosaicKey };

export const ProjectSidebar: React.VFC<ProjectSidebarProps> = () => {
  const { projectId } = useRouterParams();
  const resourceList = useReduxSelector(projectResourceListSelector(projectId));
  const modelList = useReduxSelector(modelResourceListSelector(projectId));

  const dispatch = useReduxDispatch();

  const openProjectResourceDialog = () => {
    dispatch(uiSlice.actions.toggledProjectResourceDialog({ isOpen: true }));
  };

  const openProjectModelDialog = () => {
    dispatch(uiSlice.actions.toggledProjectModelDialog({ isOpen: true }));
  };

  return (
    <MosaicViewManager
      collapseOptions={collapseOptions}
      containerClassName={classNames(
        'sd-sidebar-container',
        'sd-project-sidebar'
      )}
      initialValue={initialValue}
      splitOptions={splitOptions}
    >
      <ProjectSidebarSection
        header={copy.components.project.resources.title}
        id={ProjectSidebarTileId.RESOURCES}
        itemRenderer={(item, { index }) => {
          if (item.type === ProjectResourceType.FILE_UPLOAD) {
            return (
              <ProjectResourceFileUploadCard
                key={item.value}
                fileId={item.value}
                idx={index}
              />
            );
          }
          if (item.type === ProjectResourceType.VIRTUAL_GRAPH) {
            return (
              <ProjectResourceVirtualGraphCard
                key={item.value}
                idx={index}
                virtualGraphId={item.value}
              />
            );
          }
          return null;
        }}
        items={resourceList}
        onAdd={openProjectResourceDialog}
        placeholder={copy.components.project.resources.filter}
      />
      <ProjectSidebarSection
        header={copy.components.project.models.title}
        id={ProjectSidebarTileId.MODELS}
        itemRenderer={(item, { index }) => (
          <ProjectResourceModelCard
            key={item.value}
            idx={index}
            modelId={item.value}
          />
        )}
        items={modelList}
        onAdd={openProjectModelDialog}
        placeholder={copy.components.project.models.filter}
      />
    </MosaicViewManager>
  );
};
